// src/utils/contactLogStore.js
// One app-wide "Log a contact" panel (components/customer360/LogContactModal,
// mounted at the root). Any screen opens it with openContactLog(); the draft and
// the AI talking points live here, so the rep can move around the whole site
// mid-call. The open draft is mirrored to sessionStorage to survive a reload.
import { fmtLocalInput } from "./dates";

const KEY = "cbc_contact_log_draft";

const blankDraft = () => ({
  channel: "phone",
  direction: "outbound",
  subject: "",
  notes: "",
  contactedAt: fmtLocalInput(new Date().toISOString()),
  nextFollowUp: "",
  purpose: "",
});

const closed = { open: false, minimized: false, account: null, draft: blankDraft(), talking: null };
let state = restore() || closed;
const listeners = new Set();
let onSavedCb = null; // the opener's callback; not persisted

function restore() {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw || !localStorage.getItem("cbc_user")) return null; // signed out → drop it
    const s = JSON.parse(raw);
    if (!s?.open || !s?.account?.accountId) return null;
    return { ...closed, ...s, draft: { ...blankDraft(), ...s.draft } };
  } catch {
    return null;
  }
}

function persist() {
  try {
    if (state.open) sessionStorage.setItem(KEY, JSON.stringify(state));
    else sessionStorage.removeItem(KEY);
  } catch {}
}

function emit(next) {
  state = next;
  persist();
  listeners.forEach((fn) => fn());
}

export const getContactLogState = () => state;

export function subscribeContactLog(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export const hasDraftText = (s = state) => !!(s.open && (s.draft.notes.trim() || s.draft.subject.trim()));

/**
 * Open the panel for an account. initial = { channel, subject, notes,
 * nextFollowUp, purpose, _onSaved }. Returns false when the rep kept the
 * draft already open for another account.
 */
export function openContactLog({ accountId, accountName = "", accountNumber = "", initial = null, onSaved = null }) {
  if (!accountId) return false;
  const id = String(accountId);
  if (state.open && state.account?.accountId === id) {
    // Same customer: keep the draft, just bring it back up (and fill blanks).
    const d = { ...state.draft };
    if (initial?.subject && !d.subject.trim()) d.subject = initial.subject;
    if (initial?.notes && !d.notes.trim()) d.notes = initial.notes;
    if (initial?.channel && !d.notes.trim() && !d.subject.trim()) d.channel = initial.channel;
    onSavedCb = initial?._onSaved || onSaved || onSavedCb;
    emit({ ...state, minimized: false, draft: d, account: { ...state.account, accountName: accountName || state.account.accountName, accountNumber: accountNumber || state.account.accountNumber } });
    return true;
  }
  if (hasDraftText() && !window.confirm(`You have an unsaved contact note for ${state.account?.accountName || "another customer"}. Discard it and start one for ${accountName || "this customer"}?`)) {
    emit({ ...state, minimized: false });
    return false;
  }
  onSavedCb = initial?._onSaved || onSaved || null;
  emit({
    open: true,
    minimized: false,
    account: { accountId: id, accountName, accountNumber },
    draft: {
      ...blankDraft(),
      channel: initial?.channel || "phone",
      subject: initial?.subject || "",
      notes: initial?.notes || "",
      nextFollowUp: initial?.nextFollowUp || "",
      purpose: initial?.purpose || initial?.subject || "",
    },
    talking: null,
  });
  return true;
}

export function closeContactLog() {
  onSavedCb = null;
  emit({ ...closed, draft: blankDraft() });
}

export function updateContactDraft(patch) {
  if (!state.open) return;
  emit({ ...state, draft: { ...state.draft, ...(typeof patch === "function" ? patch(state.draft) : patch) } });
}

export function setContactLogMinimized(minimized) {
  emit({ ...state, minimized: !!minimized });
}

export function setContactAccountInfo(info) {
  if (!state.open) return;
  emit({ ...state, account: { ...state.account, ...info } });
}

export function setTalkingPoints(talking) {
  if (!state.open) return;
  emit({ ...state, talking });
}

// Fired after a save; every open contact-log list reloads for that account.
export const CONTACT_SAVED_EVENT = "contactlog:saved";

export function notifyContactSaved(accountId, res) {
  try { onSavedCb?.(res); } catch {}
  window.dispatchEvent(new CustomEvent(CONTACT_SAVED_EVENT, { detail: { accountId: String(accountId), id: res?.id || null } }));
}

/** Call fn whenever a contact is saved (for accountId, or any when omitted). */
export function onContactSaved(fn, accountId) {
  const h = (e) => { if (!accountId || e.detail?.accountId === String(accountId)) fn(e.detail); };
  window.addEventListener(CONTACT_SAVED_EVENT, h);
  return () => window.removeEventListener(CONTACT_SAVED_EVENT, h);
}
